import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const initialState = {
  name: "",
  organisation: "",
  email: "",
  investment_focus: "",
  message: "",
};

/**
 * Editorial contact form — no card surfaces, hairline-divided fields,
 * typography-led labels.
 */
export default function ContactForm({ sourcePage = "contact", testIdPrefix = "contact" }) {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/investor-enquiries", { ...form, source_page: sourcePage });
      setSubmitted(true);
      setForm(initialState);
      toast.success("Enquiry received. A member of our team will be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid={`${testIdPrefix}-form-success`}>
        <div className="evg-overline">Thank You</div>
        <h3 className="evg-display-md mt-6">Your enquiry has been received.</h3>
        <p className="evg-body mt-7 max-w-xl">
          A member of the Evergreen team will respond personally within two business days. For
          urgent matters, please contact investors@evergreenglobalholdings.com.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          data-testid={`${testIdPrefix}-form-reset`}
          className="mt-10 text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors"
        >
          Submit Another Enquiry →
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-form`}
      noValidate
      className="bg-white"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        <Field
          label="Name"
          name="name"
          value={form.name}
          onChange={onChange}
          required
          testId={`${testIdPrefix}-field-name`}
        />
        <Field
          label="Organisation"
          name="organisation"
          value={form.organisation}
          onChange={onChange}
          testId={`${testIdPrefix}-field-organisation`}
        />
        <Field
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          required
          testId={`${testIdPrefix}-field-email`}
        />
        <Field
          label="Investment Focus"
          name="investment_focus"
          value={form.investment_focus}
          onChange={onChange}
          placeholder="Infrastructure, Agro-Industry, Hospitality"
          testId={`${testIdPrefix}-field-focus`}
        />
      </div>

      <div className="mt-2 pt-8 border-t border-[#0A1F44]/12">
        <label className="evg-overline block" htmlFor={`${testIdPrefix}-message`}>
          Message <span className="text-[#D4AF37]">*</span>
        </label>
        <textarea
          id={`${testIdPrefix}-message`}
          name="message"
          rows={5}
          value={form.message}
          onChange={onChange}
          required
          data-testid={`${testIdPrefix}-field-message`}
          className="mt-5 w-full bg-transparent border-0 border-b border-[#0A1F44]/20 px-0 py-3 text-[15px] text-[#0A1F44] focus:outline-none focus:border-[#0A1F44] font-light resize-none"
        />
      </div>

      <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <p className="text-[12px] text-[#0A1F44]/55 max-w-md font-light leading-relaxed">
          By submitting this form you consent to Evergreen Global Holdings Ltd processing your
          information in line with our Privacy Policy.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid={`${testIdPrefix}-submit`}
          className="text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors disabled:opacity-50 self-start md:self-auto"
        >
          {submitting ? "Submitting…" : "Submit Enquiry →"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, name, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <div className="pt-8 border-t border-[#0A1F44]/12">
      <label className="evg-overline block" htmlFor={testId}>
        {label} {required ? <span className="text-[#D4AF37]">*</span> : null}
      </label>
      <input
        id={testId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-5 w-full bg-transparent border-0 border-b border-[#0A1F44]/20 px-0 py-3 text-[15px] text-[#0A1F44] focus:outline-none focus:border-[#0A1F44] font-light placeholder:text-[#0A1F44]/30"
      />
    </div>
  );
}
